import React, { useEffect, useReducer } from 'react';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { useActiveRightSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { RecommendationPanel } from './RecommendationPanel.tsx';
import {
	RECOMMENDATION_PANEL_ID,
	getProviderBridge,
	subscribeToBridgeChanges,
	RecommendationDataContext,
	RecommendationUIContext,
} from './RecommendationPanelContext.tsx';

const panelSplitter = defineMessage({
	id: 'admin-pages__performance.SafeRecommendationPanel.panel-splitter',
	defaultMessage: 'Resize recommendation panel',
	description: 'Label for the panel splitter to resize the recommendation panel',
});

const closePanelLabel = defineMessage({
	id: 'admin-pages__performance.SafeRecommendationPanel.close-panel-label',
	defaultMessage: 'Close panel',
	description: 'Aria label for the close panel button',
});

/**
 * Inner component that contains all hooks.
 * This is only rendered on the client side.
 * NOTE: This component is rendered OUTSIDE the provider tree (in right sidebar layout).
 * It uses a bridge mechanism to access data from the provider in DashboardPage.
 */
const PanelInLayoutClient = () => {
	const { formatMessage } = useIntl();
	const rightSidebarCurrentState = useActiveRightSidebarState();
	const panelId = rightSidebarCurrentState?.panelId;

	// Force re-render when bridge data changes
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	// Access data via bridge (since this component is outside the provider tree)
	const bridge = getProviderBridge();

	// Subscribe to bridge updates to trigger re-renders
	useEffect(() => {
		const unsubscribe = subscribeToBridgeChanges(() => {
			forceUpdate();
		});
		return unsubscribe;
	}, []);

	// Get current values from bridge
	const selectedCardId = bridge.uiContext?.selectedCardId ?? null;
	const dataContextValue = bridge.dataContext;
	const uiContextValue = bridge.uiContext;

	// Only render when our panel ID matches the active right sidebar panel
	if (panelId !== RECOMMENDATION_PANEL_ID) {
		return null;
	}

	// Don't render if no card is selected or no data available
	if (!selectedCardId || !dataContextValue || !uiContextValue) {
		return null;
	}

	return (
		<RecommendationDataContext.Provider value={dataContextValue}>
			<RecommendationUIContext.Provider value={uiContextValue}>
				<PanelSplitter label={formatMessage(panelSplitter)} />
				<Box xcss={panelContentStyles}>
					<RecommendationPanel
						cardId={selectedCardId}
						closePanelLabel={formatMessage(closePanelLabel)}
					/>
				</Box>
			</RecommendationUIContext.Provider>
		</RecommendationDataContext.Provider>
	);
};

/**
 * SafeRecommendationPanel is the component that should be registered in the route's layout.
 * It follows the same pattern as Rovo's SafeConversationAssistantPanel:
 * - Always rendered by the RightSidebar system
 * - Returns null when its panel ID is not active (via PanelInLayoutClient)
 * - Shows content when openRightSidebar(RECOMMENDATION_PANEL_ID) is called
 *
 * The inner PanelInLayoutClient handles returning null when the panel is not active,
 * which safely prevents rendering on SSR during route generation.
 */
export const SafeRecommendationPanel = (): React.JSX.Element => (
	<JSErrorBoundary
		fallback="unmount"
		id="right-side-recommendation-panel"
		packageName="admin-pages-performance"
	>
		<PanelInLayoutClient />
	</JSErrorBoundary>
);

const panelContentStyles = xcss({
	padding: 'space.400',
});
